import { Link } from "react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader } from "@/components/ui/loader";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { useQuery } from "@tanstack/react-query";
import { BackButton } from "@/components/ui/BackButton";
import { Badge } from "@/components/ui/badge";
import { format, parseISO } from "date-fns";
import { useNavigate } from "react-router";
import { MarkdownRender } from "@/components/ui/markdown-render";

type UserEvent = {
  eventId: string;
  name: string;
  orgId: string;
  startDate: string;
  description: string;
  orgName: string;
};

const fetchUserEvents = async (): Promise<UserEvent[]> => {
  const res = await fetch("/api/me/events");
  if (!res.ok) throw new Error("Failed to fetch events");
  return res.json();
};

export function UserEvents() {
  const navigate = useNavigate();
  const { data: events, isLoading, error } = useQuery({
    queryKey: ["userEvents"],
    queryFn: fetchUserEvents,
  });

  if (isLoading) return <Loader />;
  if (error)
    return (
      <Alert variant="destructive">
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>Could not load events.</AlertDescription>
      </Alert>
    );

  if (!events || events.length === 0) {
    return <div className="p-4 text-center text-muted-foreground">No events</div>;
  }

  return (
    <div className="space-y-4 mx-auto">
      <div className="flex justify-between items-center mb-6">
        <BackButton />
        <h1 className="text-2xl font-bold">Upcoming Events</h1>
        <div className="h-[36px] w-[36px] md:w-[80px]">
        </div>
      </div>
      <div className="grid gap-4">
        {events.length > 0 ? (
          events.map((event) => (
            <Card key={event.eventId} onClick={() => navigate(`/app/org/${event.orgId}/event/${event.eventId}`)}>
              <CardHeader>
                <CardTitle className="flex justify-between items-center">
                  <span>{event.name}</span>
                  <span className="text-muted-foreground text-sm">
                    {format(parseISO(event.startDate), 'PPP @ p')}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <MarkdownRender content={event.description.slice(0, 100)} />
                <div className="flex justify-between items-center">
                  <Link
                    className="text-primary underline hover:no-underline"
                    to={`/app/org/${event.orgId}/event/${event.eventId}`}
                  >
                    View Event
                  </Link>
                  <Badge>
                    {event.orgName}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="p-4 text-center text-muted-foreground">No upcoming events</div>
        )}
      </div>
    </div>
  );
}
